import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import { Attachment, Documentation } from 'graphql-common';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import TabBar from '@lib/components/TabBar/TabBar';
import AppContentMaxWidth from '@lib/layouts/AppLayout/AppContentMaxWidth/AppContentMaxWidth';
import TabsWrap from '@lib/layouts/AppLayout/TabsWrap/TabsWrap';
import { TabBarItem as TabBarItemType } from '@lib/components/TabBar/constants';
import FileViewerModal from '@lib/components/FileViewerModal/FileViewerModal';
import getDocumentationFilePermissions from '@lib/utils/documentation/getDocumentationFilePermissions';
import DocumentVersionNotes from '@lib/components/DocumentationPreview/DocumentVersionNotes';
import { FieldItem, Value } from '@lib/interfaces/form';
import ScanQrStickyBtn from 'components/ScanQrStickyBtn/ScanQrStickyBtn';
import { ScannerActionType } from 'routes/Scanner/enums';
import { DOCUMENT_FILTER_EMPTY_VALUES } from '../utils/useDocumentFilterFields';
import DocumentList from './DocumentList';
import getTableTabs from '../utils/getTableTabs';

type Props = {
  allCount: number;
  closeFileViewer: () => void;
  closeVersionNotes: () => void;
  collection: Documentation[];
  favoritesCount: number;
  fileViewerDocument: Documentation | null;
  filterCounter: number;
  filterFields: FieldItem[];
  filterParams: { [key: string]: Value };
  firstLoading: boolean;
  loading: boolean;
  onDetails: (v: Documentation) => void;
  onTabParamChange: (v: string, p?: object) => void;
  onToggleFavorite: (v: Documentation) => void;
  onVersionNotes: (v: Documentation) => void;
  onViewDocument: (v: Documentation) => void;
  setFilterParams: (v: { [key: string]: Value }) => void;
  tabParam: string;
  totalCount: number;
  unreadCount: number;
  versionNotesDocument: Documentation | null;
};

export default function Documents(props: Props) {
  const {
    allCount,
    closeFileViewer,
    closeVersionNotes,
    collection,
    favoritesCount,
    fileViewerDocument,
    filterCounter,
    filterFields,
    filterParams,
    firstLoading,
    loading,
    onDetails,
    onTabParamChange,
    onToggleFavorite,
    onVersionNotes,
    onViewDocument,
    setFilterParams,
    tabParam,
    totalCount,
    unreadCount,
    versionNotesDocument,
  } = props;

  const { t } = useTranslation();

  const fileViewerPermissions =
    getDocumentationFilePermissions(fileViewerDocument);

  const tableTabs = getTableTabs({
    allCount,
    favoritesCount,
    t,
    unreadCount,
  });

  const onActiveTabChange = (item: TabBarItemType) =>
    onTabParamChange(item.value);

  const onResetFilter = () =>
    setFilterParams(DOCUMENT_FILTER_EMPTY_VALUES as { [key: string]: Value });

  return (
    <PageBody>
      <TabsWrap useAppContentMaxWidth>
        <TabBar
          items={tableTabs}
          active={tabParam}
          onActiveChange={onActiveTabChange}
          withoutCrop
          variant="fullWidth"
        />
      </TabsWrap>
      <AppContentMaxWidth>
        <DocumentList
          collection={collection}
          filterCounter={filterCounter}
          filterFields={filterFields}
          filterParams={filterParams}
          firstLoading={firstLoading}
          loading={loading}
          onDetails={onDetails}
          onResetFilter={onResetFilter}
          onToggleFavorite={onToggleFavorite}
          onVersionNotes={onVersionNotes}
          onViewDocument={onViewDocument}
          setFilterParams={setFilterParams}
          tabParam={tabParam}
          totalEntries={totalCount}
        />
      </AppContentMaxWidth>
      <FileViewerModal
        attachment={
          _get(fileViewerDocument, [
            'currentVersion',
            'attachmentAttached',
          ]) as Attachment
        }
        isOpen={!!fileViewerDocument}
        documentationVersionId={fileViewerDocument?.currentVersion?.id}
        onClose={closeFileViewer}
        useDownloadTool={fileViewerPermissions.allowDownload}
        usePrintTool={fileViewerPermissions.allowPrint}
      />
      <DocumentVersionNotes
        isOpen={!!versionNotesDocument}
        notes={_get(versionNotesDocument, ['currentVersion', 'notes']) || ''}
        onClose={closeVersionNotes}
      />
      <ScanQrStickyBtn scannerActionType={ScannerActionType.FindDocuments} />
    </PageBody>
  );
}
